import { FC, useState } from 'react';
import ReusableModal from '../common/Modal';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  TextField,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { commonStyles } from '../../utils/styles/inputs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inviteUserApi } from '../../utils/apis';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../common/MessagePopup';

const SECTION_OPTIONS: { key: string; label: string }[] = [
  { key: 'agents', label: 'Agents' },
  { key: 'call_logs', label: 'Call Logs' },
  { key: 'knowledge_base', label: 'Knowledge Base' },
  { key: 'voice_library', label: 'Voice Library' },
  { key: 'phone_numbers', label: 'Phone Numbers' },
  { key: 'whatsapp_chats', label: 'WhatsApp Chats' },
  { key: 'agent_analytics', label: 'Analytics' },
  { key: 'order_management', label: 'Order Management' },
  { key: 'reservation_management', label: 'Reservation Management' },
  { key: 'store_management', label: 'Outlet Management' },
  { key: 'brand_management', label: 'Brand Management' },
  { key: 'billing', label: 'Billing' },
  { key: 'campaign', label: 'Campaign' },
];

interface InviteMemberModalProps {
  open: boolean;
  onClose: () => void;
}

const AddMemberToOrganisationModal: FC<InviteMemberModalProps> = ({
  open,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();
  const [email, setEmail] = useState<string>('');
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isDropdownOpen = Boolean(anchorEl);

  const { mutateAsync: inviteUser, isPending: isInvitePending } = useMutation({
    mutationFn: inviteUserApi,
    onSuccess: () => {
      showAlert('Invitation sent successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['get-organization'] });
      setEmail('');
      setSelectedSections([]);
      onClose();
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Failed to send invitation',
        'error'
      );
    },
  });

  const handleToggleSection = (key: string) => {
    setSelectedSections(prev =>
      prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
    );
  };

  const handleSelectAll = () => {
    if (selectedSections.length === SECTION_OPTIONS.length) {
      setSelectedSections([]);
    } else {
      setSelectedSections(SECTION_OPTIONS.map(s => s.key));
    }
  };

  const handleInvite = async () => {
    await inviteUser({ email, sections: selectedSections });
  };

  return (
    <>
      <ReusableModal open={open} onClose={onClose} title="Invite Member">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <FormLabel sx={commonStyles.formLabel}>
              Enter email to invite member
            </FormLabel>
            <TextField
              id="email"
              name="email"
              placeholder="example@gmail.com"
              required
              fullWidth
              variant="outlined"
              sx={commonStyles.textField}
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </FormControl>

          <FormControl fullWidth>
            <Box
              sx={{
                display: 'flex',
                mb: 0.5,
              }}
            >
              <FormLabel sx={commonStyles.formLabel}>
                Select permissions to grant access
              </FormLabel>
            </Box>
            <Box>
              <Button
                size="medium"
                onClick={e => setAnchorEl(e.currentTarget)}
                endIcon={<span style={{ fontSize: 10 }}>▼</span>}
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  fontSize: '14px',
                  color: '#6B6F80',
                  border: '1px solid #DCE0E7',
                  borderRadius: '6px',
                  bgcolor: '#FFFFFF',
                  width: '100%',
                  px: 1.5,
                  py: 1,
                  minWidth: 0,
                  justifyContent: 'space-between',
                  '&:hover': {
                    bgcolor: '#F8F9FC',
                    borderColor: '#E0E0E0',
                  },
                }}
              >
                {selectedSections.length > 0
                  ? `${selectedSections.length} permission${
                      selectedSections.length > 1 ? 's' : ''
                    }`
                  : 'Select permissions'}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={isDropdownOpen}
                onClose={() => setAnchorEl(null)}
                PaperProps={{
                  sx: {
                    maxHeight: 350,
                    width: 260,
                    borderRadius: '8px',
                    boxShadow: '0px 4px 16px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <MenuItem
                  onClick={handleSelectAll}
                  dense
                  sx={{ py: 0.75, borderBottom: '1px solid #E5E7EB' }}
                >
                  <ListItemText
                    primary={
                      selectedSections.length === SECTION_OPTIONS.length
                        ? 'Deselect All'
                        : 'Select All'
                    }
                    primaryTypographyProps={{ fontSize: '13px' }}
                  />
                </MenuItem>
                <Divider />
                {SECTION_OPTIONS.map(section => {
                  const isSelected = selectedSections.includes(section.key);
                  return (
                    <MenuItem
                      key={section.key}
                      onClick={() => handleToggleSection(section.key)}
                      dense
                      sx={{ py: 0.5 }}
                    >
                      <Checkbox
                        checked={isSelected}
                        size="small"
                        sx={{ p: 0.5, mr: 1 }}
                      />
                      <ListItemText
                        primary={section.label}
                        primaryTypographyProps={{ fontSize: '13px' }}
                      />
                    </MenuItem>
                  );
                })}
              </Menu>
            </Box>
          </FormControl>

          <Button
            variant="contained"
            sx={{ width: '100%', textTransform: 'none' }}
            disabled={
              !email || selectedSections.length === 0 || isInvitePending
            }
            onClick={() => void handleInvite()}
          >
            {isInvitePending ? 'Sending Invitation...' : 'Send Invitation'}
          </Button>
        </Box>
      </ReusableModal>
      <MessagePopup alert={alert} setAlert={setAlert} />
    </>
  );
};

export default AddMemberToOrganisationModal;
